import {Container, ContainerModule, ContainerModuleLoadOptions} from 'inversify'
import {DI, DI_VARIATIONS} from '../types'

import {SessionServiceInterface} from '@/src/core/domain/session'
import {MobileSessionService} from '@/src/infrastructure/session/mobile-session.service'
import {WebClientSessionService} from '@/src/infrastructure/session/web-client-session.service'
import {
  ApiClientService,
  ApiRequestBuilder,
  ApiResponseHandler,
  MobileApiRequestBuilder,
  MobileApiResponseHandler,
  NextApiEndpointProvider,
} from '@/src/infrastructure/services/api'
import {ClientCurrentSessionResolver} from '@/src/infrastructure/services/api/client-current-session-resolver'
import {ClientSessionResponseProcessor} from '@/src/infrastructure/services/api/client-session-response-processor'
import {ClientIpResolver} from '@/src/infrastructure/services/request-context/client-ip-resolver'

import {
  IApiClientService,
  IApiClientServiceFactory,
  IApiRequestBuilder,
  IApiResponseHandler,
  IEndpointResolver,
} from '@/src/ports/services/api'
import {IClientIpResolver} from '@/src/ports/services/request-context'
import {ICurrentSessionResolver} from '@/src/ports/services/current-session-resolver'
import {ISessionResponseProcessor} from '@/src/ports/services/session-response-processor'

import {isMobile} from '@/src/shared/utils/functions'

const initializeModule = ({bind}: ContainerModuleLoadOptions) => {
  bind<IEndpointResolver>(DI_VARIATIONS.NextApiEndpointProvider)
    .to(NextApiEndpointProvider)
    .inRequestScope()

  bind<IApiRequestBuilder>(DI_VARIATIONS.MobileApiRequestBuilder)
    .to(MobileApiRequestBuilder)
    .inRequestScope()
  bind<IApiRequestBuilder>(DI_VARIATIONS.ApiRequestBuilder).to(ApiRequestBuilder).inRequestScope()

  bind<IApiResponseHandler>(DI_VARIATIONS.ApiResponseHandler).to(ApiResponseHandler).inRequestScope()
  bind<IApiResponseHandler>(DI_VARIATIONS.MobileApiResponseHandler)
    .to(MobileApiResponseHandler)
    .inRequestScope()

  bind<IEndpointResolver>(DI.IEndpointResolver)
    .toDynamicValue(context => context.get<IEndpointResolver>(DI_VARIATIONS.NextApiEndpointProvider))
    .inRequestScope()

  bind<IApiRequestBuilder>(DI.IApiRequestBuilder)
    .toDynamicValue(context =>
      isMobile()
        ? context.get<IApiRequestBuilder>(DI_VARIATIONS.MobileApiRequestBuilder)
        : context.get<IApiRequestBuilder>(DI_VARIATIONS.ApiRequestBuilder),
    )
    .inRequestScope()

  bind<IApiResponseHandler>(DI.IApiResponseHandler)
    .toDynamicValue(context =>
      isMobile()
        ? context.get<IApiResponseHandler>(DI_VARIATIONS.MobileApiResponseHandler)
        : context.get<IApiResponseHandler>(DI_VARIATIONS.ApiResponseHandler),
    )
    .inRequestScope()

  bind<IClientIpResolver>(DI.IClientIpResolver).to(ClientIpResolver).inSingletonScope()

  bind<ICurrentSessionResolver>(DI.ICurrentSessionResolver).to(ClientCurrentSessionResolver)
  bind<ISessionResponseProcessor>(DI.ISessionResponseProcessor).to(ClientSessionResponseProcessor)

  bind<SessionServiceInterface>(DI_VARIATIONS.MobileSessionService)
    .to(MobileSessionService)
    .inRequestScope()
  bind<SessionServiceInterface>(DI_VARIATIONS.WebClientSessionService)
    .to(WebClientSessionService)
    .inRequestScope()
  bind<SessionServiceInterface>(DI.SessionServiceInterface)
    .toDynamicValue(context =>
      isMobile()
        ? context.get<SessionServiceInterface>(DI_VARIATIONS.MobileSessionService)
        : context.get<SessionServiceInterface>(DI_VARIATIONS.WebClientSessionService),
    )
    .inRequestScope()

  bind<IApiClientService>(DI.IApiClientService).to(ApiClientService).inRequestScope()

  bind<IApiClientServiceFactory>(DI.IApiClientServiceFactory).toFactory(context => target => {
    if (target) {
      const container = new Container({defaultScope: 'Request'})
      container
        .bind(DI.IApiClientService)
        .toDynamicValue(() => {
          const client = context.get<IApiClientService>(DI.IApiClientService)
          const endpointResolver =
            target === 'api'
              ? context.get<IEndpointResolver>(DI_VARIATIONS.NextApiEndpointProvider)
              : context.get<IEndpointResolver>(DI_VARIATIONS.NextApiEndpointProvider)
          client.requestBuilder.endpointResolver = endpointResolver
          return client
        })
        .inRequestScope()
      return container.get<IApiClientService>(DI.IApiClientService)
    }
    return context.get<IApiClientService>(DI.IApiClientService)
  })
}

export const APIModule = new ContainerModule(initializeModule)
